import { VCPM } from '../models/project.model';
import { DataProvider } from '../models/data-provider.model';
import { AppConstants } from '../models/app-constants.model';
import { PcsSizingEngineRequest } from '../models/pcs-sizing-engine-request.model';
import { PcsSizingEngineResponse } from '../models/pcs-sizing-engine-response.model';
import {
  getWorkloadDomainRecommendationRequest,
  getManagementDomainRecommendationRequest,
  resolveEffectiveHardwareConfiguration,
  SizingRequestContext,
} from './sizing-request.adapter';

/**
 * Display labels for ReadyNode profile codes - single source of truth for
 * ManagementDomainComponent.getProfileCards and WorkloadDomainComponent.getProfileCards,
 * previously duplicated verbatim across both.
 */
export const READY_NODE_PROFILE_SIZE_LABELS: Record<string, string> = {
  'vSAN-HCI-SM': 'Small',
  'vSAN-HCI-MED': 'Medium',
  'vSAN-HCI-LRG': 'Large',
  'vSAN-SC-SM': 'Small',
  'vSAN-SC-MED': 'Medium',
  'vSAN-SC-LRG': 'Large',
};

/**
 * True for a ReadyNode-based/HARDWARE_FIRST profileRecommendations response: alternate tier
 * candidates exist, but none is genuinely marked recommended - there's no "recommended tier"
 * concept once the user has already picked an exact ReadyNode. Deliberately checks "no entry has
 * isRecommended === true" rather than "every entry is exactly null/undefined" - the engine may
 * represent "not recommended" as `false` or as `null`/omitted depending on version, and treating
 * only the null/undefined form as ReadyNode-based silently misses the `false` form (the real
 * observed cause of the 4th "Selected ReadyNode" card failing to render - the 3 tier cards and the
 * customization grid gate both already treat "not real no-preference" leniently, so the 4th-card
 * check must too). A genuine No-Preference response always has exactly one entry with
 * isRecommended === true, so it's never misclassified by this broader check.
 *
 * NOTE: `hardwarePreference: HARDWARE_FIRST` is also sent for cpu-based and use-existing-cluster
 * workloads (see resolveHardwarePreference in sizing-request.adapter.ts) - this function only
 * detects the response SHAPE, not which HARDWARE_FIRST sub-mode produced it. Callers that need to
 * gate ReadyNode-based-only UI must additionally confirm the workload's real input
 * hardwareConfigMode is 'readynode-based' (see getRecommendedProfileCode's second parameter).
 */
export function isHardwareFirstProfileResponse(
  recomm: PcsSizingEngineResponse.WorkloadRecommendation,
  scope: 'ALL' | 'OTHER_SIDE' = 'ALL',
  otherClusterType?: 'HCI' | 'SON',
): boolean {
  // 'OTHER_SIDE' (SON/HCI only) exists because a mixed dHCI response always carries a CON entry that
  // is isRecommended: true by definition, which would make the whole-array check below report "not
  // ReadyNode-based" even when every SON tier is false (SON customized with a ReadyNode) - the 4th
  // "Selected ReadyNode" card then never rendered. Non-mixed responses are identical in both scopes.
  const { conSide, otherSide } = partitionProfileRecommendationsBySide(recomm, otherClusterType);
  // A scoped caller (focused HCI/SON cluster) only ever looks at its own cluster type's tiers, even when
  // that leaves none - another type's tiers must never decide for it.
  const entries = otherClusterType || (scope === 'OTHER_SIDE' && conSide.length && otherSide.length) ? otherSide : (recomm.profileRecommendations ?? []);
  return !!entries.length && !entries.some((p) => p.isRecommended === true);
}

/**
 * Splits a dHCI (CON+SON) "no-preference" workload's `profileRecommendations` into the CON side
 * (always exactly one entry, no tiers, engine-recommended by definition) and the "other" side (SON
 * tiers, or HCI tiers, or nothing). Non-dHCI responses (HCI-only, pure-CON, pure-SON) always yield
 * one empty bucket - callers must treat that as "not actually mixed" and fall back to today's
 * whole-array resolution, not iterate the non-empty bucket alone, so behavior for those responses
 * stays byte-identical to before this split existed. Partitions on `clusters[0]?.clusterType`
 * rather than `workloadId`, because a SON entry's `workloadId` is the storage cluster's own name,
 * not the parent workload's id - an implementation detail of the engine response, not a reliable
 * discriminator.
 *
 * A dHCI Compute side that references an existing HCICluster yields BOTH HCI-typed and SON-typed
 * tiers in `otherSide`. Callers rendering one focused HCI/SON cluster pass `otherClusterType` to
 * narrow `otherSide` to that cluster's own tiers; entries with no clusterType at all (non-dHCI
 * responses) always pass through, and omitting the parameter keeps the unscoped behavior.
 */
export function partitionProfileRecommendationsBySide(
  recomm: PcsSizingEngineResponse.WorkloadRecommendation,
  otherClusterType?: 'HCI' | 'SON',
): { conSide: PcsSizingEngineResponse.WorkloadRecommendation[]; otherSide: PcsSizingEngineResponse.WorkloadRecommendation[] } {
  const all = recomm.profileRecommendations ?? [];
  const conSide = all.filter((p) => p.clusters?.[0]?.clusterType === 'CON');
  const otherSide = all.filter((p) => {
    const type = p.clusters?.[0]?.clusterType;
    return type !== 'CON' && (!otherClusterType || !type || type === otherClusterType);
  });
  return { conSide, otherSide };
}

/**
 * Whether a saved workload-level `selectedReadyNodeRecommendation` is the customization of the
 * focused cluster. A dHCI workload whose Compute side references an HCICluster holds an HCI AND a
 * SON cluster, both "not CON" - so a CON-vs-not comparison would hand the SON's customization to
 * the HCI cluster's page. A tagged selection (`clusterType`) is therefore compared exactly. An
 * untagged (pre-tag) save, or a focus with no known clusterType, keeps the original CON-vs-not rule
 * (untagged: judged by its result's first cluster).
 */
export function selectedReadyNodeMatchesClusterType(
  selected: PcsSizingEngineResponse.SelectedReadyNodeRecommendation | undefined,
  focusedClusterType: 'HCI' | 'CON' | 'SON' | undefined,
): boolean {
  if (!selected) {
    return false;
  }
  if (selected.clusterType !== undefined && focusedClusterType !== undefined) {
    return selected.clusterType === focusedClusterType;
  }
  const selectedIsCon = selected.clusterType !== undefined
    ? selected.clusterType === 'CON'
    : selected.result.clusters?.[0]?.clusterType === 'CON';
  return selectedIsCon === (focusedClusterType === 'CON');
}

/**
 * The profile code the engine recommended (isRecommended: true) - used as the default
 * pre-selection before the user has ever explicitly chosen a tier. When the response is
 * ReadyNode-based-shaped (see isHardwareFirstProfileResponse) AND the caller confirms (via
 * `inputHardwareConfigMode`) that the workload's real input mode is genuinely 'readynode-based'
 * (HCI/SON), returns the SELECTED_READYNODE_PROFILE_CODE sentinel instead - the raw top-level
 * `recomm.clusters` (the actual configured ReadyNode's sizing) is the correct default
 * "recommendation" in that case. `inputHardwareConfigMode` is optional and omitted by internal pure
 * callers (e.g. getEffectiveWorkloadRecommendation below) - without it this always returns undefined
 * for a ReadyNode-based response, unchanged from before this parameter existed.
 *
 * CON ('esxi' - its own equivalent terminal customization mode, never 'readynode-based') is handled
 * as its own case entirely, BEFORE the tier search, and unconditionally on whether a SON/HCI side
 * is also present:
 * - CON-ONLY (conSide non-empty, otherSide empty - a standalone compute-only workload/cluster, not
 *   mixed dHCI): CON's lone profileRecommendations entry is always isRecommended: true by
 *   definition (see partitionProfileRecommendationsBySide's own doc comment - "no tiers, always
 *   isRecommended"), so leaving it to the generic tier search would always short-circuit with its
 *   own real profile code, never reaching the esxi-sentinel branch.
 * - MIXED dHCI (both conSide and otherSide present): the SON/HCI side's tier-recommendation status
 *   is irrelevant to CON's own default - SON almost always HAS a genuinely recommended tier (see
 *   the mixed-response test below), which would otherwise short-circuit this function with SON's
 *   code before CON's own 'esxi' mode was ever considered. This was the real root cause of a mixed
 *   dHCI workload's CON-side dynamic "Custom ESXi Server" card never appearing for an input-only
 *   ESXi customization, even after inputHardwareConfigMode correctly resolved to 'esxi' - the
 *   CON-ONLY branch alone (added first) never fired because otherSide was non-empty.
 * In both shapes the real tier-recommendation concept simply doesn't apply to CON; the only
 * question is whether the input mode is genuinely 'esxi'.
 */
export function getRecommendedProfileCode(
  recomm: PcsSizingEngineResponse.WorkloadRecommendation,
  inputHardwareConfigMode?: string,
  otherClusterType?: 'HCI' | 'SON',
): string | undefined {
  const { conSide, otherSide } = partitionProfileRecommendationsBySide(recomm, otherClusterType);
  if (conSide.length && !otherSide.length) {
    return inputHardwareConfigMode === 'esxi' ? AppConstants.SELECTED_READYNODE_PROFILE_CODE : undefined;
  }
  if (conSide.length && otherSide.length) {
    if (inputHardwareConfigMode === 'esxi') {
      return AppConstants.SELECTED_READYNODE_PROFILE_CODE;
    }
  }
  const searchSpace = otherClusterType || (conSide.length && otherSide.length) ? otherSide : recomm.profileRecommendations ?? [];
  const tierCode = searchSpace.find((p) => p.isRecommended)?.profileCode?.value;
  if (tierCode) {
    return tierCode;
  }
  if (inputHardwareConfigMode === 'readynode-based' && isHardwareFirstProfileResponse(recomm, 'OTHER_SIDE', otherClusterType)) {
    return AppConstants.SELECTED_READYNODE_PROFILE_CODE;
  }
  return undefined;
}

/**
 * Resolves which candidate of a "no-preference" workload/management-domain recommendation is
 * currently active: the customized "Selected ReadyNode" result (once the sentinel is active, either
 * explicitly chosen or - for a ReadyNode-based response - the implicit default), the user's
 * explicitly chosen tier, the engine-recommended tier, or the raw recommendation unchanged
 * (readynode-based/cpu-based workloads with no `profileRecommendations` at all short-circuit above).
 * Single source of truth for WorkloadDomainComponent.getEffectiveWorkloadRecommendation /
 * ManagementDomainComponent.getEffectiveManagementRecommendation, and for the Recommendation
 * Summary's own cluster/resource-aggregate computation (std-gf-recommendation.component.ts) -
 * previously duplicated across all three with slight drift between copies.
 *
 * For a mixed dHCI response (both a CON side and a SON/HCI side present), each side is resolved
 * independently and their `.clusters` are concatenated. `selectedProfileCode`/
 * `selectedReadyNodeRecommendation` are shared, workload-level fields - only one side is ever
 * customized at a time (confirmed product rule: CON and SON/HCI customization are mutually
 * exclusive per workload), so whichever side the active Recalculate result's own clusters belong to
 * is the customized one; the other side always falls back to its normal (CON: single-entry, SON/HCI:
 * tier) resolution. See resolveProfileSide for the shared per-side logic.
 *
 * `conHardwareConfigMode`/`otherHardwareConfigMode` are optional, threaded through to
 * getRecommendedProfileCode exactly like that function's own `inputHardwareConfigMode` parameter
 * (see its doc comment) - needed so resolveProfileSide can recognize an IMPLICIT (never explicitly
 * clicked, never saved) ReadyNode/ESXi default for a mixed dHCI side (see resolveProfileSide's own
 * doc comment for why this matters). Two separate parameters (not one, reused for both sides) because
 * CON and SON/HCI are configured independently at the input wizard - a mixed workload can genuinely
 * have CON implicitly defaulted to 'esxi' AND its SON/HCI side independently implicitly defaulted to
 * 'readynode-based' at the same time (this is unrelated to the "only one EXPLICIT/saved Recalculate
 * result at a time" product rule above - that rule is about the shared `selectedProfileCode`/
 * `selectedReadyNodeRecommendation` fields, not about each side's own unsaved input default). Passing
 * the same single mode value to both sides used to be harmless by coincidence (resolveProfileSide's
 * own check is gated on `sideClusterType`, so a mode string can only ever satisfy one side's check at
 * a time) - but that only works when the caller already knows which ONE side is active (e.g.
 * WorkloadDomainComponent's Cluster View, scoped to a single focused cluster); callers with no such
 * "focused side" concept (any view that renders a whole workload's cards at once, e.g. the
 * Recommendation Summary / VCF Instance / CON-SON-HCI cluster-group list pages) need to resolve and
 * pass each side's own real mode independently - see resolveWorkloadHardwareConfigModes below.
 */
export function getEffectiveWorkloadRecommendation(
  recomm: PcsSizingEngineResponse.WorkloadRecommendation,
  conHardwareConfigMode?: string,
  otherHardwareConfigMode?: string,
): PcsSizingEngineResponse.WorkloadRecommendation {
  if (!recomm.profileRecommendations?.length) {
    return recomm;
  }
  const { conSide, otherSide } = partitionProfileRecommendationsBySide(recomm);
  if (!conSide.length || !otherSide.length) {
    return restoreDroppedClusterTypes(
      recomm,
      otherSide,
      resolveSingleSideRecommendation(recomm, conHardwareConfigMode ?? otherHardwareConfigMode),
    );
  }
  const conWinner = resolveProfileSide(recomm, conSide, conHardwareConfigMode);
  const sonWinner = resolveProfileSide(recomm, otherSide, otherHardwareConfigMode);
  return { ...recomm, clusters: [...conWinner.clusters, ...sonWinner.clusters] };
}

/**
 * View-agnostic counterpart to WorkloadDomainComponent's own private
 * getEffectiveHardwareConfigForWorkload: resolves BOTH sides' real effective input
 * hardwareConfigMode for a given workload at once (CON's via `computeClusterHardwareConfiguration`/
 * `computeCluster`, SON/HCI's via `storageClusterHardwareConfiguration`/`storageCluster`, a
 * 'use-existing-cluster' mirror on either resolved one hop further via
 * resolveEffectiveHardwareConfiguration exactly as that component does) - for callers with no
 * "focused cluster" concept of their own (see getEffectiveWorkloadRecommendation's own doc comment
 * for why a single shared mode value isn't enough for those callers). For a plain HCI (non-dHCI)
 * workload, `storageClusterHardwareConfiguration`/`storageCluster` are both undefined, so `otherMode`
 * harmlessly resolves to the same value as `conMode` (both fall back to the shared
 * `workload.hardwareConfiguration`) - getEffectiveWorkloadRecommendation's own single-side branch
 * only ever consults one of the two anyway for that shape. Returns `{}` (both undefined) when either
 * `workload` or `domain` can't be resolved, so callers can invoke this unconditionally.
 */
export function resolveWorkloadHardwareConfigModes(
  workload: VCPM.Workload | undefined,
  domain: VCPM.WorkloadDomain | undefined,
  storageClusters: VCPM.StorageCluster[] | undefined,
): { conMode?: string; otherMode?: string } {
  if (!workload || !domain) {
    return {};
  }
  // Some fixtures/legacy workloads carry no hardwareConfiguration at all (e.g. a bare
  // clusterConfiguration-only shape) - resolveEffectiveHardwareConfiguration requires a real `hw`
  // to read `.hardwareConfigMode` off of, so guard rather than passing it undefined.
  const conHw = workload.computeClusterHardwareConfiguration ?? workload.hardwareConfiguration;
  const otherHw = workload.storageClusterHardwareConfiguration ?? workload.hardwareConfiguration;
  return {
    conMode: conHw ? resolveEffectiveHardwareConfiguration(conHw, domain, storageClusters, workload.computeCluster).hardwareConfigMode : undefined,
    otherMode: otherHw ? resolveEffectiveHardwareConfiguration(otherHw, domain, storageClusters, workload.storageCluster).hardwareConfigMode : undefined,
  };
}

/**
 * A dHCI workload whose Compute side references an HCICluster has an HCI AND a SON cluster but no
 * CON entry, so it takes the single-side path above. A tier entry (or a customization slice from a
 * cluster-level Recalculate) only holds ONE cluster type, so resolving to it silently drops the
 * other type's cluster - e.g. picking an HCI tier removed the customized/tier-less SON cluster from
 * the nav, and picking the ReadyNode card brought it back. This appends every cluster type the
 * workload's own `clusters` carry that the resolved entry lacks, each from its own best source: the
 * tagged saved customization of that type, else that type's recommended tier, else the raw
 * top-level clusters (the tier-less / customized-at-input case). Returns `resolved` untouched
 * whenever it already covers every type, so every other shape is unchanged.
 */
function restoreDroppedClusterTypes(
  recomm: PcsSizingEngineResponse.WorkloadRecommendation,
  otherSide: PcsSizingEngineResponse.WorkloadRecommendation[],
  resolved: PcsSizingEngineResponse.WorkloadRecommendation,
): PcsSizingEngineResponse.WorkloadRecommendation {
  if (resolved === recomm || !recomm.clusters?.length) {
    return resolved;
  }
  const present = new Set((resolved.clusters ?? []).map((c) => c.clusterType));
  const missingTypes = [...new Set(recomm.clusters.map((c) => c.clusterType))].filter((t) => !!t && !present.has(t));
  if (!missingTypes.length) {
    return resolved;
  }
  const selected = recomm.selectedReadyNodeRecommendation;
  const restored = missingTypes.flatMap((type) => {
    const custom = selected && selected.clusterType === type ? selected.result.clusters?.filter((c) => c.clusterType === type) : undefined;
    if (custom?.length) {
      return custom;
    }
    const tier = otherSide.find((p) => p.isRecommended && p.clusters?.[0]?.clusterType === type);
    return tier?.clusters?.length ? tier.clusters : recomm.clusters.filter((c) => c.clusterType === type);
  });
  return { ...resolved, clusters: [...(resolved.clusters ?? []), ...restored] };
}

function resolveSingleSideRecommendation(
  recomm: PcsSizingEngineResponse.WorkloadRecommendation,
  inputHardwareConfigMode?: string,
): PcsSizingEngineResponse.WorkloadRecommendation {
  const code = recomm.selectedProfileCode ?? getRecommendedProfileCode(recomm, inputHardwareConfigMode);
  if (code === AppConstants.SELECTED_READYNODE_PROFILE_CODE) {
    return recomm.selectedReadyNodeRecommendation?.result ?? recomm;
  }
  const matched = recomm.profileRecommendations!.find((p) => p.profileCode?.value === code);
  if (matched) {
    return matched;
  }
  // A defined-but-non-matching code (e.g. a mismatched selectedProfileCode cascaded onto this recomm
  // from a sibling workload's own tier pick - see cascadeSelectedProfileCodeToClusterMembers) means
  // some tier IS active, just not one of this side's own candidates - fall back to this side's first
  // real candidate rather than the raw, unresolved recomm (whose top-level clusters can be empty for
  // a tiered response). An undefined code legitimately means "no tier concept applies yet" (e.g. a
  // ReadyNode-based response with no explicit selection) - recomm itself is the correct result there,
  // unchanged from before this fallback existed.
  return code !== undefined ? (recomm.profileRecommendations![0] ?? recomm) : recomm;
}

/**
 * Resolves ONE side (CON, or SON/HCI) of a mixed dHCI recomm. The active Recalculate sentinel/result
 * is shared across both sides (see getEffectiveWorkloadRecommendation's own doc comment), so it's
 * only applied to the side whose own clusters it actually belongs to (matched by clusterType) -
 * otherwise this side falls back to its normal resolution: CON's single entry, or the SON/HCI side's
 * explicit/recommended tier. Mirrors resolveSingleSideRecommendation's rule, just scoped to `side`
 * instead of the full `profileRecommendations` array.
 *
 * When there's no explicit pick, AND this side's real input mode is its own terminal customization
 * sentinel (CON: 'esxi', unconditionally - see getRecommendedProfileCode's own CON branches for why
 * CON's `isRecommended` can't be trusted either way; SON/HCI: 'readynode-based' with no tier
 * genuinely recommended) with no saved `selectedReadyNodeRecommendation` to read from (the
 * "customized only at the sizing wizard input, never Recalculated/saved at output" case - see Issue
 * 4/Addendum 2 in the bugfix plan), `side[0]`/a tier search are NOT the right fallback: `side[0]` is
 * a separate "what-if" profileRecommendations estimate entry with its own distinct (and often
 * incomplete - e.g. missing connectedClusters/connectedWorkloads sharing metadata) Cluster record,
 * not the real customized sizing. The real customized sizing is the raw top-level `recomm.clusters`,
 * filtered to this side's clusterType - the same source getProfileCards()'s own legacy-fallback
 * branch already uses to build the dynamic picker card, so this keeps the picker's card and the
 * summary cards below it in sync. Checked directly against `sideClusterType` (not by delegating to
 * getRecommendedProfileCode, which resolves ONE sentinel verdict for whichever side the caller's own
 * `inputHardwareConfigMode` describes) so resolving the OTHER side in the same mixed workload never
 * mistakenly inherits it.
 */
function resolveProfileSide(
  recomm: PcsSizingEngineResponse.WorkloadRecommendation,
  side: PcsSizingEngineResponse.WorkloadRecommendation[],
  inputHardwareConfigMode?: string,
): PcsSizingEngineResponse.WorkloadRecommendation {
  const sideClusterType = side[0]?.clusters?.[0]?.clusterType;
  const customResult = recomm.selectedReadyNodeRecommendation?.result;
  // customResult can carry BOTH sides' clusters for a mixed dHCI workload - only ever take the
  // entries matching this side's own clusterType, so the other (uncustomized) side never inherits
  // a stale/duplicated copy of the customized side's clusters.
  if (customResult && recomm.selectedProfileCode === AppConstants.SELECTED_READYNODE_PROFILE_CODE && customResult.clusters?.some((c) => c.clusterType === sideClusterType)) {
    return { ...customResult, clusters: customResult.clusters.filter((c) => c.clusterType === sideClusterType) };
  }
  const explicitCode = recomm.selectedProfileCode !== AppConstants.SELECTED_READYNODE_PROFILE_CODE ? recomm.selectedProfileCode : undefined;
  if (explicitCode === undefined) {
    const sideDefaultsToSentinel = sideClusterType === 'CON'
      ? inputHardwareConfigMode === 'esxi'
      : inputHardwareConfigMode === 'readynode-based' && isHardwareFirstProfileResponse(recomm, 'OTHER_SIDE');
    if (sideDefaultsToSentinel) {
      const raw = recomm.clusters?.filter((c) => c.clusterType === sideClusterType);
      if (raw?.length) {
        return { ...recomm, clusters: raw };
      }
    }
  }
  const code = explicitCode ?? side.find((p) => p.isRecommended)?.profileCode?.value;
  return side.find((p) => p.profileCode?.value === code) ?? side[0] ?? recomm;
}

/**
 * Forces the mode that makes resolveHardwarePreference() return HARDWARE_FIRST and
 * buildHardwareConfigurationPayload() populate `chassis` from the customized spec, without
 * touching either of those functions. Callers only ever apply this to a throwaway clone of the
 * project - the workload's real persisted hardwareConfigMode stays 'no-preference'.
 */
export function forceHardwareFirstRecalculationConfig(hw: VCPM.HardwareConfiguration): VCPM.HardwareConfiguration {
  return { ...hw, hardwareConfigMode: 'readynode-based' };
}

/**
 * Builds a full workload-domain recommendation request with exactly one workload's hardware
 * configuration swapped for the customized ReadyNode spec - every other workload in every other
 * domain keeps its real, unmodified configuration. The existing, ported-as-is
 * getWorkloadDomainRecommendationRequest() is reused unchanged; only the input project clone differs.
 */
export function getWorkloadDomainRecalculationRequest(
  project: VCPM.Project,
  requirementsId: string,
  workloadId: string,
  customizedHardwareConfiguration: VCPM.HardwareConfiguration,
  context: SizingRequestContext,
): PcsSizingEngineRequest.DesignRequest | null {
  const clone: VCPM.Project = JSON.parse(JSON.stringify(project));
  const domain = clone.data.workloadDomains?.find((d) => String(d.id) === String(requirementsId));
  const workload = domain?.workloadList?.find((w) => w.id === workloadId);
  if (!workload) return null;
  workload.hardwareConfiguration = forceHardwareFirstRecalculationConfig(customizedHardwareConfiguration);
  return withoutProfileTierComparison(getWorkloadDomainRecommendationRequest(clone, context));
}

/**
 * Builds a full management-domain recommendation request with the management domain's hardware
 * configuration swapped for the customized ReadyNode spec.
 */
export function getManagementDomainRecalculationRequest(
  project: VCPM.Project,
  customizedHardwareConfiguration: VCPM.HardwareConfiguration,
  context: SizingRequestContext,
): PcsSizingEngineRequest.DesignRequest | null {
  const clone: VCPM.Project = JSON.parse(JSON.stringify(project));
  if (!clone.data.managementDomain?.configuration) return null;
  clone.data.managementDomain.configuration.hardwareConfiguration =
    forceHardwareFirstRecalculationConfig(customizedHardwareConfiguration);
  return withoutProfileTierComparison(getManagementDomainRecommendationRequest(clone, context));
}

/**
 * Flags every workload in a request to skip the engine's informational tier comparison. Only for a
 * request whose response is reduced to one workload's slice (Recalculate, Logic Bound): each HARDWARE_FIRST
 * ReadyNode workload in it would otherwise have three tiers sized and discarded. Safe for all of them,
 * the target and every sibling alike - the Small/Medium/Large cards come from the saved recommendation,
 * which this never touches, and a NO_PREFERENCE workload's tiers are its sizing, which the engine keeps.
 *
 * Mutates and returns the request it is given, which the builders here always construct fresh.
 */
export function withoutProfileTierComparison<T extends PcsSizingEngineRequest.DesignRequest | null>(request: T): T {
  request?.design?.requirements?.forEach((requirement) =>
    requirement.workloads?.forEach((workload) => {
      workload.skipProfileTiers = true;
    }),
  );
  return request;
}

/**
 * Extracts a single workload's recommendation from a full-domain response - used to discard every
 * sibling workload's (re)computed result after a recalculation request, so an unrelated workload's
 * already-persisted state is never touched.
 */
export function extractSingleWorkloadRecommendation(
  response: PcsSizingEngineResponse.RecommendationResponse | undefined | null,
  requirementsId: string,
  workloadId: string,
): PcsSizingEngineResponse.WorkloadRecommendation | undefined {
  const domainRecomm = response?.result?.recommendations?.find((r) => r.requirementsId === requirementsId);
  return domainRecomm?.workloadRecommendations?.find((w) => w.workloadId === workloadId);
}

/**
 * A workload referencing a shared cluster via 'use-existing-cluster' must keep that sentinel in its
 * own persisted hardwareConfigMode - only the owning cluster entity's real mode should ever land
 * there (see ClusterHardwareConfigComponent.onPicked() for dHCI, WorkloadConfigurationComponent
 * .onHciClusterPicked() for HCI, both of which apply this same rule at initial-pick time). Without
 * this, fanning a shared cluster's canonical hardwareConfiguration out to a referencer would
 * silently overwrite the referencer's sentinel with the cluster's real mode, corrupting which radio
 * it reopens on. Moved here (from a private method on ProjectDetailsState) so both the input-wizard
 * fan-out actions (UpdateHciClusterConfiguration et al.) and the output-side cluster recalculation
 * flow (getClusterRecalculationRequest/RecalculateClusterHardwareConfiguration) share one
 * implementation instead of drifting.
 */
export function syncReferencedHardwareConfiguration(
  current: VCPM.HardwareConfiguration | undefined,
  incoming: VCPM.HardwareConfiguration,
): VCPM.HardwareConfiguration {
  return current?.hardwareConfigMode === 'use-existing-cluster'
    ? { ...incoming, hardwareConfigMode: 'use-existing-cluster' }
    : incoming;
}

/** Kind discriminator for the three canonical, shared cluster entities (VCPM.HCICluster /
 *  ComputeCluster / StorageCluster) - mirrors each entity's own `type` field. */
export type ClusterEntityKind = 'HCI' | 'COMPUTE' | 'STORAGE';

/** Converts the input-entity-side discriminator (ClusterEntityKind) to the engine-response-side
 *  vocabulary (Cluster.clusterType / SelectedReadyNodeRecommendation.clusterType). The two
 *  vocabularies exist because ClusterEntityKind additionally distinguishes a dHCI workload's two
 *  halves (COMPUTE/STORAGE) from a plain HCI cluster's single entity kind ('HCI'), while
 *  clusterType only cares which side a given Cluster/customization belongs to. */
export function clusterEntityKindToClusterType(kind: ClusterEntityKind): 'HCI' | 'CON' | 'SON' {
  return kind === 'COMPUTE' ? 'CON' : kind === 'STORAGE' ? 'SON' : 'HCI';
}

/**
 * Resolves an output-side cluster identity back to the canonical input-side cluster entity that owns
 * its real hardwareConfiguration, via the anchor workload's own ClusterReference (`computeCluster` /
 * `storageCluster`) rather than by matching names. The ClusterReference's `.id` is always correct -
 * it's assigned atomically alongside the entity itself, by both the explicit "use existing cluster"
 * wizard actions and `migrateReusableClusterEntitiesInPlace` - unlike a response `Cluster`'s display
 * name, which `buildClusterDisplayName` decorates with `clusterSplitName` (e.g. "HCICluster-13
 * Cluster 1") and can never be relied on to equal the entity's own `.name` again once split segments
 * are present. Once resolved, every further lookup (resolveClusterMembers, the
 * Update*ClusterConfiguration actions) uses the entity's own stable `id`.
 */
export function resolveClusterEntity(
  domain: VCPM.WorkloadDomain,
  storageClusters: VCPM.StorageCluster[] | undefined,
  workload: VCPM.Workload,
  clusterType: 'HCI' | 'CON' | 'SON',
): { id: string; kind: ClusterEntityKind } | undefined {
  if (clusterType === 'SON') {
    const refId = workload.storageCluster?.id;
    const cluster = refId ? storageClusters?.find((sc) => sc.id === refId) : undefined;
    return cluster ? { id: cluster.id, kind: 'STORAGE' } : undefined;
  }
  // Both HCI and CON (dHCI-compute) sides resolve through the same single computeCluster
  // reference now - the referenced pool entity's own `type` tells us which kind it is, so we
  // filter the domain pool by the wanted type rather than reading a kind-specific reference field.
  const wantType = clusterType === 'CON' ? 'COMPUTE' : 'HCI';
  const refId = workload.computeCluster?.id;
  const cluster = refId ? domain.clusterConfigurations?.find((cc) => cc.id === refId && cc.type === wantType) : undefined;
  return cluster ? { id: cluster.id, kind: cluster.type } : undefined;
}

/**
 * Anchor-free counterpart of resolveClusterEntity for Cluster View: resolves the focused cluster by its
 * own unique, immutable name instead of going through whichever workload happened to list it first.
 * A cluster shared by several workloads (e.g. an HCI cluster reused as a dHCI compute cluster) can have
 * an anchor workload whose reference doesn't resolve to it, which left the cluster un-customizable.
 */
export function resolveClusterEntityByName(
  domain: VCPM.WorkloadDomain,
  storageClusters: VCPM.StorageCluster[] | undefined,
  clusterType: 'HCI' | 'CON' | 'SON',
  clusterName: string | undefined,
): { id: string; kind: ClusterEntityKind } | undefined {
  if (!clusterName) {
    return undefined;
  }
  if (clusterType === 'SON') {
    const cluster = storageClusters?.find((sc) => sc.name === clusterName);
    return cluster ? { id: cluster.id, kind: 'STORAGE' } : undefined;
  }
  const wantType = clusterType === 'CON' ? 'COMPUTE' : 'HCI';
  const cluster = domain.clusterConfigurations?.find((cc) => cc.name === clusterName && cc.type === wantType);
  return cluster ? { id: cluster.id, kind: cluster.type } : undefined;
}

/** One workload's reference to a shared cluster: which of its local-mirror hardware fields
 *  (`hardwareConfiguration` | `computeClusterHardwareConfiguration` | `storageClusterHardwareConfiguration`)
 *  corresponds to that reference, and which domain (by `WorkloadDomain.id`, stringified to match
 *  `requirementsId`) owns the workload - needed because a Storage cluster (SON) is project-wide and
 *  can be referenced by workloads in OTHER domains than the one the cluster was originally created
 *  in (mirrors UpdateStorageClusterConfiguration's own project-wide fan-out scan), unlike HCI/CON
 *  entities which live inside one domain's own `clusterConfigurations` and can only ever be referenced
 *  from within that same domain. */
export interface ClusterMember {
  requirementsId: string;
  workloadId: string;
  side: 'hci' | 'compute' | 'storage';
}

/**
 * Every workload referencing the cluster entity identified by `clusterId`, scoped to the correct
 * side for a dHCI workload (a single workload can independently reference a CON cluster on its
 * compute side and a SON cluster on its storage side - only the requested side is ever returned, so
 * a customization targeting one side never touches the other). For 'SON', scans every domain in
 * `workloadDomains` (a Storage cluster can be shared cross-domain); for 'HCI'/'CON', scanning is
 * still effectively single-domain since those entities can only ever be referenced from within their
 * own owning domain, but the full list is accepted uniformly so callers don't need to branch.
 */
export function resolveClusterMembers(
  workloadDomains: VCPM.WorkloadDomain[],
  clusterId: string,
  clusterType: 'HCI' | 'CON' | 'SON',
): ClusterMember[] {
  const side: ClusterMember['side'] = clusterType === 'CON' ? 'compute' : clusterType === 'SON' ? 'storage' : 'hci';
  const members: ClusterMember[] = [];
  workloadDomains.forEach((domain) => {
    (domain.workloadList ?? []).forEach((w) => {
      // 'hci' and 'compute' sides both resolve through the same computeCluster reference now -
      // the caller already knows which cluster type it's recalculating (HCI vs CON), so no
      // kind-specific reference field is needed to disambiguate them here.
      const ref = side === 'storage' ? w.storageCluster : w.computeCluster;
      if (ref?.id === clusterId) {
        members.push({ requirementsId: String(domain.id), workloadId: w.id!, side });
      }
    });
  });
  return members;
}

/**
 * Business rule: ReadyNode customization is allowed on an HCI/SON cluster only if its original
 * hardware preference wasn't cpu-based. Hardware configuration is singular and cluster-owned (never
 * independently set per referencing workload - a referencer only ever mirrors it, via
 * 'use-existing-cluster' or a direct copy), so this is a single check against the cluster entity's
 * own canonical mode, not a per-member gate.
 */
export function isClusterReadyNodeCustomizable(
  cluster: Pick<VCPM.HCICluster | VCPM.StorageCluster, 'hardwareConfiguration'>,
): boolean {
  return cluster.hardwareConfiguration.hardwareConfigMode !== 'cpu-based';
}

/**
 * Cluster-scoped generalization of getWorkloadDomainRecalculationRequest: swaps
 * customizedHardwareConfiguration onto every workload referencing the cluster (identified by its
 * stable entity id, not by name), on one project clone, then rebuilds the full request exactly
 * once - still a single API call regardless of how many workloads share the cluster.
 *
 * Unlike the single-workload getWorkloadDomainRecalculationRequest, this does NOT call
 * forceHardwareFirstRecalculationConfig - that helper unconditionally relabels the mode to
 * 'readynode-based', which would silently corrupt an ESXi customization (CON clusters) into a
 * ReadyNode one. The caller (a customization component) is expected to already set the correct
 * terminal hardwareConfigMode on `customizedHardwareConfiguration`, exactly as
 * NoPreferenceHardwareCustomizationComponent already does today for the single-workload flow.
 *
 * Also updates the cluster entity's OWN canonical hardwareConfiguration within the clone (not just
 * workload mirrors): resolveEffectiveHardwareConfiguration (used by chassis-building) resolves a
 * 'use-existing-cluster' member's chassis through to the cluster entity, never through the member's
 * own (already-synced-but-sentinel-pinned) mirror fields - so a referencer's preview chassis would
 * otherwise still reflect the stale pre-recalculation config. Every member's mirror is still synced
 * too, via syncReferencedHardwareConfiguration, so a referencer's sentinel survives exactly as it
 * would when the wizard's own Update*ClusterConfiguration actions apply the same change at Save
 * time - the preview and the persisted result stay byte-for-byte consistent.
 */
export function getClusterRecalculationRequest(
  project: VCPM.Project,
  requirementsId: string,
  clusterId: string,
  clusterType: 'HCI' | 'CON' | 'SON',
  customizedHardwareConfiguration: VCPM.HardwareConfiguration,
  context: SizingRequestContext,
): { request: PcsSizingEngineRequest.DesignRequest; members: ClusterMember[] } | null {
  const clone: VCPM.Project = JSON.parse(JSON.stringify(project));
  const domain = clone.data.workloadDomains?.find((d) => String(d.id) === String(requirementsId));
  if (!domain || !clone.data.workloadDomains) return null;
  const members = resolveClusterMembers(clone.data.workloadDomains, clusterId, clusterType);
  if (!members.length) return null;

  const clusterEntity = clusterType === 'SON'
    ? clone.data.clusterConfigurations?.find((c) => c.id === clusterId)
    : domain.clusterConfigurations?.find((c) => c.id === clusterId);
  if (clusterEntity) {
    clusterEntity.hardwareConfiguration = customizedHardwareConfiguration;
  }

  members.forEach((m) => {
    const memberDomain = clone.data.workloadDomains!.find((d) => String(d.id) === m.requirementsId);
    const workload = memberDomain?.workloadList?.find((w) => w.id === m.workloadId);
    if (!workload) return;
    if (m.side === 'compute') {
      workload.computeClusterHardwareConfiguration = syncReferencedHardwareConfiguration(workload.computeClusterHardwareConfiguration, customizedHardwareConfiguration);
    } else if (m.side === 'storage') {
      workload.storageClusterHardwareConfiguration = syncReferencedHardwareConfiguration(workload.storageClusterHardwareConfiguration, customizedHardwareConfiguration);
    } else {
      workload.hardwareConfiguration = syncReferencedHardwareConfiguration(workload.hardwareConfiguration, customizedHardwareConfiguration);
    }
  });
  const request = withoutProfileTierComparison(getWorkloadDomainRecommendationRequest(clone, context));
  return request ? { request, members } : null;
}

/**
 * Multi-workload generalization of extractSingleWorkloadRecommendation: all-or-nothing - returns
 * undefined (callers must treat as total failure, applying no partial preview) if ANY expected
 * member is missing from the response, rather than silently returning a partial map. One
 * request/one response has no partial-HTTP-failure mode; the only failure to guard against is the
 * engine response omitting one member's slice. Takes each member's own `requirementsId` (not a
 * single shared one) since a SON cluster's members can span multiple domains/recommendations.
 */
export function extractClusterWorkloadRecommendations(
  response: PcsSizingEngineResponse.RecommendationResponse | undefined | null,
  members: ClusterMember[],
): Map<string, PcsSizingEngineResponse.WorkloadRecommendation> | undefined {
  const map = new Map<string, PcsSizingEngineResponse.WorkloadRecommendation>();
  for (const m of members) {
    const domainRecomm = response?.result?.recommendations?.find((r) => r.requirementsId === m.requirementsId);
    const wr = domainRecomm?.workloadRecommendations?.find((w) => w.workloadId === m.workloadId);
    if (!wr) return undefined;
    map.set(m.workloadId, wr);
  }
  return map;
}

/**
 * SizingRequestContext.wldComponents/mgmtDomainComponents are normally the software-component
 * CATALOG fetched from the backend when the user visits the input-configuration wizard
 * (GetComponents action) - buildComponentsPayload()/buildComponentsForMgmtPayload() use it only to
 * look up the `id` of a few sub-components (HCX-IX/MANAGER/NE/SRG/WAN, AON-COLLECTOR/PLATFORM,
 * VCF_OPERATIONS/FLEET_MANAGEMENT/VCF_OPERATIONS_COLLECTOR) that aren't stored as their own
 * `swComponents` entries. A user who reaches the output screen's Recalculate flow without visiting
 * that wizard in the current session has no such catalog loaded - rather than making a fresh
 * backend call for it (component selections aren't changing here, only one workload's hardware),
 * this rebuilds an equivalent catalog from the domain's OWN already-persisted recommendation
 * (`Recommendation.components`), which was resolved against the real catalog the one time the
 * domain was actually sized. `SoftwareComponent.brickSize` (what was actually applied) stands in
 * for `DataProvider.Component.defaultSizing` - a strictly more accurate value than any generic
 * "default", since it's the exact sizing already reflected in this project's saved result.
 */
export function getSavedDomainComponentsCatalog(
  components: PcsSizingEngineResponse.SoftwareComponent[] | undefined,
): DataProvider.Component[] {
  return (components ?? []).map((sc) => ({
    id: sc.id,
    name: sc.productName,
    version: sc.version || '',
    minNumberOfVms: 0,
    defaultSizing: (sc.brickSize as DataProvider.DefaultSizing) || DataProvider.DefaultSizing.Small,
    sizing: [],
  }));
}
